<template>
  <div :class="notificationClassObj">
    <div :class="$style.titleContainer">
      <Icon :class="$style.icon" />
      <span :class="$style.title">{{ title }}</span>
      <span :class="$style.text">{{ text }}</span>
    </div>
    <div :class="$style.buttonsContainer">
      <CloseIcon :class="$style.closeIcon" />
    </div>
  </div>
</template>

<script setup>
import { computed, useCssModule } from "vue";
import { NOTIFICATION_TYPES } from "@/constants";
import InfoIcon from "@/components/Icon/Info.vue";
import SuccessIcon from "@/components/Icon/Success.vue";
import CloseIcon from "@/components/Icon/Close.vue";
import DangerIcon from "@/components/Icon/Danger.vue";

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    required: false,
  },
});

const $style = useCssModule();

const Icon = computed(() => {
  if (!props.type) return InfoIcon;
  if (props.type === NOTIFICATION_TYPES.INFO) return InfoIcon;
  if (props.type === NOTIFICATION_TYPES.SUCCESS) return SuccessIcon;
  if (props.type === NOTIFICATION_TYPES.ERROR) return DangerIcon;
  if (props.type === NOTIFICATION_TYPES.WARNING) return DangerIcon;
});

const notificationClassObj = computed(() => ({
  [$style.notification]: true,
  [$style.notificationSuccess]: props.type === NOTIFICATION_TYPES.SUCCESS,
  [$style.notificationError]: props.type === NOTIFICATION_TYPES.ERROR,
  [$style.notificationWarning]: props.type === NOTIFICATION_TYPES.WARNING,
}));
</script>

<style lang="scss">
.Vue-Toastification__container {
  .Vue-Toastification__toast {
    padding: 0;
    background: transparent !important;
    min-height: unset;
  }
}
</style>

<style module lang="scss">
.notification {
  display: flex;
  width: 400px;
  padding: 8px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  border-radius: 8px;
  border: 1px solid #d1c9ff;
  background: #e8e4ff;

  .titleContainer {
    display: flex;
    align-items: center;
    gap: 8px;

    .icon {
      width: 24px;
      height: 24px;
      color: #5423c5;
    }

    .title {
      font-family: "Noto Sans";
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 20px;
      color: #5423c5;
    }

    .text {
      color: #5423c5;

      font-family: "Noto Sans";
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }
  }

  .buttonsContainer {
    display: flex;
    align-items: center;
    gap: 8px;

    .closeIcon {
      width: 24px;
      height: 24px;
      color: #5423c5;
      cursor: pointer;
    }
  }

  &Success {
    border: 1px solid #a2ebc4;
    background: #d1f5e1;

    .titleContainer {
      .icon {
        color: #219656;
      }

      .title {
        color: #219656;
      }

      .text {
        color: #219656;
      }
    }

    .buttonsContainer {
      .closeIcon {
        color: #219656;
      }
    }
  }

  &Error {
    border: 1px solid #ffafaf;
    background: #ffd7d7;

    .titleContainer {
      .icon {
        color: #eb382a;
      }

      .title {
        color: #eb382a;
      }

      .text {
        color: #eb382a;
      }
    }

    .buttonsContainer {
      .closeIcon {
        color: #eb382a;
      }
    }
  }

  &Warning {
    border: 1px solid #fadec3;
    background: #fdf4e6;

    .titleContainer {
      .icon {
        color: #b77513;
      }

      .title {
        color: #b77513;
      }

      .text {
        color: #b77513;
      }
    }

    .buttonsContainer {
      .closeIcon {
        color: #b77513;
      }
    }
  }
}
</style>
